import PlayerUtils from '../../../../../shared/utils/player';
import { ITrack } from '../../../../../shared/types/interfaces/player';
import { PlayerManagerProps as Props } from '../../../../../shared/types/props/player';
import { PlayerState } from '../../../../../shared/types/enums/player';
import { store } from '@shared/store/store';
import { useCallback, useEffect, useRef } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';
import {
  playerEndAction,
  updateNextEpisode,
} from '../../../../../shared/store/thunks/player.thunk';
import {
  clearRetryPosition,
  mediaSelector,
  retry,
  setCurrentTime,
  setDuration,
  setError,
  setPosition,
  setRange,
  setReady,
  setState,
  setTracks,
} from '../../../../../shared/store/slices/media.slice';

/**
 * ListenerManager component for Shaka Player.
 * Manages all event listeners for the Shaka Player instance, including playback events,
 * error handling, track changes, and analytics tracking.
 * Updates the Redux store with player state, position, duration, and track information.
 *
 * @param {Props} props - Component props containing Shaka player instance, video element, and playback parameters
 * @returns {null} This component doesn't render anything
 */
function ListenerManager({
  shaka: player,
  video,
  bookmark,
  start,
  end,
}: Props) {
  const dispatch = useAppDispatch();
  const { audio, error, live, state, subtitles, userInteraction } =
    useAppSelector(mediaSelector);
  // Track if playback has ended to prevent multiple end events
  const endedRef = useRef(false);
  // Reference to retry timeout for error handling
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep user interaction state in ref for event handlers
  const userInteractionRef = useRef(userInteraction);
  userInteractionRef.current = userInteraction;

  // Updaters

  /**
   * Updates playback data including duration, position, and seek range.
   * Calculates position differently for live vs VOD content.
   * Dispatches updates to Redux store for current time, duration, position, and range.
   */
  const updateData = useCallback(() => {
    const range = player?.seekRange();

    // Calculate time data
    const duration = live
      ? (range?.end || 0) - (range?.start || 0)
      : end || video?.duration || 0;

    const position = live
      ? Date.now() -
        (duration -
          Math.max((video?.currentTime || 0) - (range?.start || 0), 0)) *
          1000
      : Math.max(video?.currentTime || 0, bookmark || 0);

    const seek = live
      ? {
          start: Date.now() - (duration || 0) * 1000,
          end: Date.now(),
        }
      : { start: start || 0, end: duration };

    // If the end is surpassed, finish playback
    if (!endedRef.current && end && position > duration) {
      dispatch(playerEndAction());
      endedRef.current = true;
    }

    // Update media slice
    dispatch(setRange({ seek }));
    dispatch(setCurrentTime(video?.currentTime || 0));
    dispatch(setDuration(duration));
    dispatch(setPosition({ position, seek: false })); // Set position last is best
    dispatch(updateNextEpisode());
  }, [player, video, live]);

  const updateAudio = useCallback(() => {
    if (audio?.language && player) {
      player.configure({
        abr: {
          enabled: false,
        },
      });
      player.selectAudioLanguage(audio.language);
      player.configure({
        abr: {
          enabled: true,
        },
      });
    }
  }, [audio, player]);

  /**
   * Updates subtitle track selection and visibility.
   * Enables/disables subtitles based on track selection and temporarily disables ABR
   */
  const updateSubtitles = useCallback(() => {
    if (player) {
      if (subtitles && subtitles.id) {
        player.configure({
          abr: {
            enabled: false,
          },
        });
        player.setTextTrackVisibility(true);
        player.selectTextLanguage(subtitles.language);
        player.configure({
          abr: {
            enabled: true,
          },
        });
      } else {
        player.setTextTrackVisibility(false);
      }
    }
  }, [subtitles, player]);

  /**
   * Updates available audio and subtitle tracks from the player.
   * Maps player track information to the application's track format.
   */
  const updateTracks = useCallback(() => {
    // Set audio and subtitle tracks
    const audio =
      player?.getAudioLanguages().map((item, index) => {
        return {
          id: index.toString(),
          language: item?.toLowerCase(),
        } as ITrack;
      }) || [];
    const subtitles =
      player?.getTextLanguages().map((item, index) => {
        return { id: index.toString(), language: item } as ITrack;
      }) || [];
    dispatch(setTracks({ audio, subtitles }));
  }, [player]);

  // Effect hooks

  // Reset ready state when video source changes
  useEffect(() => {
    dispatch(setReady(false));
  }, [video?.src]);

  // Update audio track when audio selection changes
  useEffect(() => {
    updateAudio();
  }, [audio]);

  // Update subtitle track when subtitle selection changes
  useEffect(() => {
    updateSubtitles();
  }, [subtitles]);

  // Main effect: Set up all player and video event listeners
  useEffect(() => {
    const seeking = state === PlayerState.SEEKING;

    /**
     * Handles the 'canplay' event
     * Marks player as ready and initializes tracks and playback data.
     */
    const onCanPlay = () => {
      dispatch(setReady(true));
      updateData();
      updateTracks();
    };

    /**
     * Handles the 'ended' event when playback completes.
     * Prevents multiple end events from being dispatched.
     */
    const onEnded = () => {
      if (!endedRef.current) {
        dispatch(playerEndAction());
        endedRef.current = true;
      }
    };

    /**
     * Handles player errors with retry logic.
     * Waits 30 seconds before attempting retry or dispatching error.
     */
    const onError = (event: {
      detail: {
        code: number;
        data: {
          data: (number | string)[];
        }[];
      };
    }) => {
      retryRef.current && clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => {
        const { media } = store.getState();
        if (!media.error.retry) {
          dispatch(retry());
        } else {
          const error = PlayerUtils.parseShakaError(event);

          if (error) {
            dispatch(setError(error));
          }
        }
      }, 30 * 1000);
    };

    /**
     * Handles the 'loadstart' event when media loading begins.
     */
    const onLoaded = () => {
      dispatch(setState(PlayerState.LOADED));
    };

    /**
     * Handles the 'pause' event.
     * No action required as pause state is managed elsewhere.
     */
    const onPause = () => {
      // No action required
    };

    /**
     * Handles the 'play' event when playback starts.
     * Only updates state if not currently seeking.
     */
    const onPlay = () => {
      !seeking && dispatch(setState(PlayerState.PLAY));
    };

    /**
     * Handles the 'playing' event when playback is active.
     * Restores retry position if available after error recovery.
     */
    const onPlaying = () => {
      !seeking && dispatch(setState(PlayerState.PLAYING));
      if (error.retryPosition) {
        dispatch(setPosition({ position: error.retryPosition, seek: true }));
        dispatch(clearRetryPosition());
      }
    };

    /**
     * Handles the 'seek' event when seeking begins.
     */
    const onSeek = () => {
      dispatch(setState(PlayerState.SEEK));
    };

    /**
     * Handles the 'seeked' event when seeking completes.
     */
    const onSeeked = () => {
      dispatch(setState(PlayerState.SEEKED));
    };

    /**
     * Handles the 'stalled' event when playback stalls due to buffering.
     */
    const onStalled = () => {
      dispatch(setState(PlayerState.WAITING));
    };

    /**
     * Handles the 'timeupdate' event to update playback position.
     */
    const onTimeUpdate = () => {
      updateData();
    };

    /**
     * Handles the 'waiting' event when player is waiting for data.
     */
    const onWaiting = () => {
      dispatch(setState(PlayerState.WAITING));
    };

    // Register Shaka Player event listeners
    if (player) {
      player.addEventListener('error', onError as () => void);
    }
    // Register HTML5 video element event listeners
    if (video) {
      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('ended', onEnded);
      video.addEventListener('loadstart', onLoaded);
      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlay);
      video.addEventListener('playing', onPlaying);
      video.addEventListener('seek', onSeek);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('stalled', onStalled);
      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('waiting', onWaiting);
    }
    // Cleanup: Remove all event listeners on unmount
    return () => {
      if (player) {
        player.removeEventListener('error', onError as () => void);
      }
      if (video) {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('loadstart', onLoaded);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('seek', onSeek);
        video.removeEventListener('stalled', onStalled);
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('waiting', onWaiting);
      }
    };
  }, [player, video, state]);

  // This component doesn't render anything, it only manages event listeners
  return null;
}

export default ListenerManager;
